import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers';
import type { EventMediaSettingsPatchDto } from '@/types/dtos/media/media-settings.dto';
import { GET_EVENT_QUERY_KEY } from '@/state/queries/events/use-get-event';
import {
  mergeEventMediaSettingsInSettingsQueryCache,
  refetchFlowsAfterEventMediaSettingsChange,
  type PatchEventMediaSettingsResponse,
} from '@/state/mutations/media/patch-event-media-settings-cache';

export const usePatchEventMediaSettingsMutation = (options?: { onSuccess?: () => void }) => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ eventId, mediaSettings }: { eventId: string; mediaSettings: EventMediaSettingsPatchDto }) => {
      const token = await getToken();
      return patchEventMediaSettings(eventId, mediaSettings, token ?? '');
    },
    onSuccess: async (data, variables) => {
      mergeEventMediaSettingsInSettingsQueryCache(queryClient, variables.eventId, data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_QUERY_KEY] }),
        refetchFlowsAfterEventMediaSettingsChange(queryClient),
      ]);
      options?.onSuccess?.();
    },
  });
};

const patchEventMediaSettings = async (eventId: string, mediaSettings: EventMediaSettingsPatchDto, token: string) => {
  const url = `${import.meta.env.VITE_API_URL}/events/${eventId}/settings/media`;
  return sendJSONRequest({
    url,
    method: METHOD.PATCH,
    body: { mediaSettings },
    token,
  }) as Promise<PatchEventMediaSettingsResponse>;
};
